// Trivia Night on the big host screen (registry `Table3D`): what everyone in
// the venue sees — the lobby card, the question with its four answers and the
// countdown, the reveal (right answer, how many picked each, fastest), the
// standings and the final podium. drei <Text> (troika) like the rest of the
// app's 3D text. Also drives the sounds for people at the game.

import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { Billboard, Text } from '@react-three/drei';
import { noRaycast } from '../../VR/vrUi';
import { useGames } from '../gameStore';
import type { Table3DProps } from '../types';
import { ANSWER_COLORS, ANSWER_DIM, FACE_Z, LETTERS, PODIUM_TOP, PODIUM_X, PODIUM_Z, SCREEN_H, SCREEN_W, SCREEN_Y } from './triviaLayout';
import { categoryLabel, fmtPts, optionsLine, secs, standings, toLocal, triviaOf, useTimeLeft, type TriviaState } from './triviaClient';
import { sfx } from './triviaSounds';

const GOLD = '#ffd166';
const TEAL = '#00c2b3';
const WHITE = '#f4f7fb';
const MUTED = '#9fb0c2';

type TextProps = React.ComponentProps<typeof Text>;
function T(props: TextProps) {
  return <Text raycast={noRaycast} color={WHITE} anchorX="center" anchorY="middle" {...props} />;
}

function Panel({ w, h, color, x = 0, y = 0, z = 0, opacity = 1 }: { w: number; h: number; color: string; x?: number; y?: number; z?: number; opacity?: number }) {
  return (
    <mesh position={[x, y, z]} raycast={noRaycast as any}>
      <planeGeometry args={[w, h]} />
      <meshBasicMaterial color={color} toneMapped={false} transparent={opacity < 1} opacity={opacity} />
    </mesh>
  );
}

/** Shrinks long questions so they fit the question area. */
const questionSize = (s: string) => (s.length > 120 ? 0.12 : s.length > 85 ? 0.14 : s.length > 55 ? 0.16 : 0.18);

/** The thin countdown bar under the header (smooth, per frame). */
function TimerBar({ g }: { g: TriviaState }) {
  const ref = useRef<THREE.Mesh>(null);
  const mat = useRef<THREE.MeshBasicMaterial>(null);
  const W = SCREEN_W - 0.2;
  useFrame(() => {
    const m = ref.current;
    if (!m || g.endsAt === null) return;
    const left = g.pausedLeft ?? Math.max(0, toLocal(g.endsAt) - Date.now());
    const f = Math.max(0.0001, Math.min(1, left / g.timerMs));
    m.scale.x = f;
    m.position.x = -W / 2 + (W * f) / 2;
    mat.current?.color.set(f < 0.25 ? '#ff6b5b' : TEAL);
  });
  return (
    <group position={[0, 0.85, 0.002]}>
      <Panel w={W} h={0.035} color="#1c2733" />
      <mesh ref={ref} position={[0, 0, 0.001]} raycast={noRaycast as any}>
        <planeGeometry args={[W, 0.035]} />
        <meshBasicMaterial ref={mat} color={TEAL} toneMapped={false} />
      </mesh>
    </group>
  );
}

function AnswerBox({ i, text, g }: { i: number; text: string; g: TriviaState }) {
  const x = i % 2 === 0 ? -0.95 : 0.95;
  const y = i < 2 ? -0.13 : -0.6;
  const rv = g.phase === 'reveal' ? g.reveal : null;
  const isRight = !!rv && !rv.voided && rv.correct === i;
  const dim = !!rv && !isRight;
  return (
    <group position={[x, y, 0.004]}>
      {isRight && <Panel w={1.92} h={0.48} color="#ffffff" z={-0.001} />}
      <Panel w={1.86} h={0.42} color={dim ? ANSWER_DIM[i] : ANSWER_COLORS[i]} />
      <mesh position={[-0.8, 0, 0.002]} raycast={noRaycast as any}>
        <circleGeometry args={[0.13, 32]} />
        <meshBasicMaterial color={dim ? '#0b0f14' : '#ffffff'} toneMapped={false} transparent opacity={dim ? 0.6 : 0.95} />
      </mesh>
      <T position={[-0.8, 0, 0.004]} fontSize={0.15} color={dim ? MUTED : ANSWER_COLORS[i]} fontWeight={700 as any}>{LETTERS[i]}</T>
      <T position={[-0.62, 0, 0.004]} anchorX="left" fontSize={text.length > 30 ? 0.085 : 0.105} maxWidth={1.3} lineHeight={1.1}
        color={dim ? MUTED : '#ffffff'} outlineWidth={dim ? 0 : 0.004} outlineColor="#000">{text}</T>
      {rv && <T position={[0.84, 0, 0.004]} anchorX="right" fontSize={0.11} color={isRight ? '#ffffff' : MUTED}>{`${isRight ? '✓ ' : ''}${rv.counts[i] ?? 0}`}</T>}
    </group>
  );
}

function names(g: TriviaState, seats: number[], max = 4) {
  const n = seats.map((s) => g.names[s] ?? `Player ${s + 1}`);
  return n.length > max ? `${n.slice(0, max).join(', ')} +${n.length - max}` : n.join(', ');
}

function QuestionView({ g, active }: { g: TriviaState; active: number }) {
  const left = useTimeLeft(g);
  const s = left === null ? null : Math.ceil(left / 1000);
  const q = g.question!;
  const rv = g.phase === 'reveal' ? g.reveal : null;
  let status: string;
  if (rv?.voided) status = 'Question thrown out — no points';
  else if (rv) {
    const got = Object.keys(rv.picks).map(Number).filter((k) => rv.picks[k] === rv.correct);
    status = got.length
      ? `Fastest: ${g.names[rv.fastest!.seat] ?? 'someone'} (${secs(rv.fastest!.ms)})  ·  Got it: ${names(g, got)}`
      : 'Nobody got that one!';
  } else status = `${g.answered.length} of ${active} answered${g.mode === 'buzzer' ? '  ·  BUZZER: first right answer scores most' : ''}`;
  return (
    <>
      <T position={[-1.9, 0.98, 0.002]} anchorX="left" fontSize={0.1} color={MUTED}>
        {`QUESTION ${g.qNo} / ${g.total}  ·  ${categoryLabel(q.category).toUpperCase()}`}
      </T>
      {g.phase === 'question' && s !== null && (
        <T position={[1.9, 0.98, 0.002]} anchorX="right" fontSize={0.16} color={s <= 5 ? '#ff6b5b' : WHITE}>{String(s)}</T>
      )}
      {g.phase === 'reveal' && <T position={[1.9, 0.98, 0.002]} anchorX="right" fontSize={0.11} color={GOLD}>ANSWER</T>}
      {g.phase === 'question' && <TimerBar g={g} />}
      <T position={[0, 0.48, 0.002]} fontSize={questionSize(q.text)} maxWidth={SCREEN_W - 0.3} textAlign="center" lineHeight={1.15}>{q.text}</T>
      {q.choices.map((c, i) => <AnswerBox key={i} i={i} text={c} g={g} />)}
      <T position={[0, -0.98, 0.002]} fontSize={0.075} color={rv ? GOLD : MUTED} maxWidth={SCREEN_W - 0.2}>{status}</T>
    </>
  );
}

function StandingsView({ g, seats, title }: { g: TriviaState; seats: (unknown | null)[]; title: string }) {
  const rows = standings(g, seats).slice(0, 10);
  return (
    <>
      <T position={[0, 0.9, 0.002]} fontSize={0.17} color={GOLD}>{title}</T>
      {rows.map((r, k) => {
        const col = k < 5 ? -1.85 : 0.15, y = 0.55 - (k % 5) * 0.3;
        return (
          <group key={r.seat} position={[col, y, 0.002]}>
            <T anchorX="left" fontSize={0.12} color={k === 0 ? GOLD : WHITE}>{`${r.rank}.`}</T>
            <T position={[0.22, 0, 0]} anchorX="left" fontSize={0.12} maxWidth={1.05} color={WHITE}>{r.name.slice(0, 16)}</T>
            <T position={[1.7, 0, 0]} anchorX="right" fontSize={0.12} color={TEAL}>{fmtPts(r.score)}</T>
          </group>
        );
      })}
      {!rows.length && <T fontSize={0.12} color={MUTED}>No players</T>}
    </>
  );
}

function PodiumView({ g, seats }: { g: TriviaState; seats: (unknown | null)[] }) {
  // Everyone who finished (names are kept in g, so leavers still show).
  const rows = standings(g, undefined).filter((r) => seats[r.seat] || r.score > 0);
  const top = rows.slice(0, 3);
  const spots = [{ k: 1, x: -1.1, h: 0.5, c: '#c0c7d0' }, { k: 0, x: 0, h: 0.75, c: GOLD }, { k: 2, x: 1.1, h: 0.35, c: '#cd8a4f' }];
  const base = -0.72;
  return (
    <>
      <T position={[0, 0.92, 0.002]} fontSize={0.2} color={GOLD}>FINAL SCORES</T>
      {spots.map(({ k, x, h, c }) => {
        const r = top[k];
        if (!r) return null;
        return (
          <group key={k} position={[x, 0, 0.002]}>
            <Panel w={0.95} h={h} color={c} y={base + h / 2} />
            <T position={[0, base + h / 2, 0.003]} fontSize={0.2} color="#14181e">{String(r.rank)}</T>
            <T position={[0, base + h + 0.24, 0]} fontSize={0.12} maxWidth={1.05} color={WHITE}>{r.name.slice(0, 16)}</T>
            <T position={[0, base + h + 0.09, 0]} fontSize={0.095} color={TEAL}>{`${fmtPts(r.score)} pts`}</T>
          </group>
        );
      })}
      {rows.length > 3 && (
        <T position={[0, -0.95, 0.002]} fontSize={0.07} color={MUTED} maxWidth={SCREEN_W - 0.3}>
          {rows.slice(3, 10).map((r) => `${r.rank}. ${r.name.slice(0, 14)} ${fmtPts(r.score)}`).join('   ')}
        </T>
      )}
      {!rows.length && <T fontSize={0.12} color={MUTED}>Thanks for playing!</T>}
    </>
  );
}

function LobbyView({ table, def }: Pick<Table3DProps, 'table' | 'def'>) {
  const st = table?.state;
  const joined = st ? st.seats.filter(Boolean) : [];
  return (
    <>
      <T position={[0, 0.62, 0.002]} fontSize={0.34} color={GOLD} outlineWidth={0.006} outlineColor="#000">{`${def.glyph} TRIVIA NIGHT ${def.glyph}`}</T>
      <T position={[0, 0.27, 0.002]} fontSize={0.1} maxWidth={SCREEN_W - 0.3} textAlign="center" color={WHITE}>Press Join on your screen or VR panel — play from anywhere in the room</T>
      <T position={[0, 0.02, 0.002]} fontSize={0.14} color={TEAL}>{!st ? 'Loading…' : `${joined.length} ${joined.length === 1 ? 'player' : 'players'} in  ·  up to ${def.maxPlayers}`}</T>
      {st && <T position={[0, -0.27, 0.002]} fontSize={0.085} color={WHITE} maxWidth={SCREEN_W - 0.4} textAlign="center">{optionsLine(st.options)}</T>}
      {st && <T position={[0, -0.52, 0.002]} fontSize={0.085} color={MUTED}>{st.host ? `Hosted by ${st.host.name}` : 'No host — anyone who joined can start the game'}</T>}
      {joined.length > 0 && (
        <T position={[0, -0.82, 0.002]} fontSize={0.075} color={MUTED} maxWidth={SCREEN_W - 0.4} textAlign="center">
          {joined.slice(0, 20).map((s) => s!.name.slice(0, 14)).join('  ·  ')}
        </T>
      )}
    </>
  );
}

function IntroView({ g }: { g: TriviaState }) {
  const left = useTimeLeft(g);
  return (
    <>
      <T position={[0, 0.45, 0.002]} fontSize={0.36} color={GOLD}>GET READY!</T>
      <T position={[0, 0.05, 0.002]} fontSize={0.12} color={WHITE}>
        {`${g.total} questions  ·  ${g.timerMs / 1000} s each  ·  ${g.mode === 'buzzer' ? 'Buzzer mode — first right answer scores most, wrong answers cost a little' : 'Classic — answer fast for more points'}`}
      </T>
      {left !== null && <T position={[0, -0.5, 0.002]} fontSize={0.45} color={TEAL}>{String(Math.max(1, Math.ceil(left / 1000)))}</T>}
    </>
  );
}

/** Sounds for people at the game: ding on a new question, ticks in the last 5 s, right/wrong at the reveal, fanfare at the end. */
function useTriviaSounds(g: TriviaState | null, seat: number, enabled: boolean) {
  const prev = useRef<{ key: string; phase: string } | null>(null);
  const lastTick = useRef(-1);
  useEffect(() => {
    const key = g ? `${g.qNo}:${g.question?.text ?? ''}` : '';
    const phase = g?.phase ?? 'none';
    const p = prev.current;
    prev.current = { key, phase };
    if (!g || !p || !enabled) return; // nothing on first sight (joining mid-game)
    if (phase === 'question' && (p.phase !== 'question' || p.key !== key)) sfx.ding();
    if (phase === 'reveal' && p.phase === 'question' && g.reveal && !g.reveal.voided) {
      const mine = seat >= 0 ? g.reveal.picks[seat] : undefined;
      if (mine === undefined) sfx.reveal(); else if (mine === g.reveal.correct) sfx.correct(); else sfx.wrong();
    }
    if (phase === 'over' && p.phase !== 'over') sfx.fanfare();
  }, [g, seat, enabled]);
  // Countdown ticks.
  useEffect(() => {
    if (!g || g.phase !== 'question' || g.pausedLeft !== null || g.endsAt === null || !enabled) return;
    const endsAt = g.endsAt;
    const t = window.setInterval(() => {
      const s = Math.ceil((toLocal(endsAt) - Date.now()) / 1000);
      if (s !== lastTick.current && s >= 1 && s <= 5) sfx.tick(s === 1);
      lastTick.current = s;
    }, 100);
    return () => window.clearInterval(t);
  }, [g?.phase, g?.endsAt, g?.pausedLeft, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function TriviaScreen3D({ pid, def, table, seat, isHost }: Table3DProps) {
  const st = table?.state;
  const g = st?.phase === 'lobby' ? null : triviaOf(table);
  const near = useGames((s) => s.near === pid);
  useTriviaSounds(st?.phase === 'lobby' ? null : g, seat, near || seat >= 0 || isHost);
  const active = st && g ? st.seats.filter((s) => s && !(s.userId && g.kicked.includes(s.userId))).length : 0;

  let content: React.ReactNode;
  if (!st || st.phase === 'lobby' || !g) content = <LobbyView table={table} def={def} />;
  else if (g.phase === 'intro') content = <IntroView g={g} />;
  else if ((g.phase === 'question' || g.phase === 'reveal') && g.question) content = <QuestionView g={g} active={active} />;
  else if (g.phase === 'scores') content = <StandingsView g={g} seats={st.seats} title={`STANDINGS — AFTER ${g.qNo} OF ${g.total}`} />;
  else content = <PodiumView g={g} seats={st.seats} />;

  return (
    <>
      <group position={[0, SCREEN_Y, FACE_Z]}>
        {content}
        {g && g.pausedLeft !== null && g.phase !== 'over' && (
          <group position={[0, 0, 0.02]}>
            <Panel w={SCREEN_W} h={SCREEN_H} color="#000000" opacity={0.72} />
            <T position={[0, 0.08, 0.002]} fontSize={0.4} color={GOLD}>PAUSED</T>
            <T position={[0, -0.32, 0.002]} fontSize={0.1} color={WHITE}>{`The host paused the game${st?.host ? ` (${st.host.name})` : ''}`}</T>
          </group>
        )}
      </group>
      {/* Host sign over the podium */}
      <Billboard position={[PODIUM_X, PODIUM_TOP + 0.3, PODIUM_Z]}>
        <T fontSize={0.075} color={st?.host ? GOLD : MUTED} outlineWidth={0.005} outlineColor="#000">
          {st?.host ? `Host: ${st.host.name}` : 'HOST'}
        </T>
      </Billboard>
    </>
  );
}

export default TriviaScreen3D;
